// ============================================================
// v2.74.0 -- Accessibility (ADA) widget
// ============================================================
// Adapted for WebaNova (React). Effects are scoped to .app so
// the widget chrome stays crisp. State persists in localStorage
// (key: webanova.a11y).
// ============================================================
import { A11Y_ICONS } from './a11y-icons.js';

const LS_KEY = 'webanova.a11y';
const POS_KEY = 'webanova.a11y.position';
const TRIGGER_SVG = A11Y_ICONS.trigger;

const PROFILES = [
  { key: 'seizure', name: 'Seizure Safe', desc: 'Reduce motion and visual triggers', ico: A11Y_ICONS.seizure,
    implies: { stopAnimations: true, colorMode: 'low-saturation' } },
  { key: 'lowVision', name: 'Low Vision Support', desc: 'Improve clarity and contrast', ico: A11Y_ICONS.lowVision,
    implies: { colorMode: 'high-contrast', readableFont: true, fontScalePct: 120 } },
  { key: 'adhd', name: 'ADHD Friendly', desc: 'Support focus and reduce distractions', ico: A11Y_ICONS.adhd,
    implies: { readingMask: true, stopAnimations: true, highlightLinks: true } },
  { key: 'cognitive', name: 'Reading & Cognitive Support', desc: 'Simplify reading and navigation', ico: A11Y_ICONS.cognitive,
    implies: { readableFont: true, highlightTitles: true, highlightLinks: true } },
  { key: 'keyboard', name: 'Keyboard Navigation', desc: 'Use website with the keyboard', ico: A11Y_ICONS.keyboard,
    implies: { highlightFocus: true } },
  { key: 'screenReader', name: 'Screen Reader Compatibility', desc: 'Optimize for screen-readers', ico: A11Y_ICONS.screenReader,
    implies: { screenReader: true } },
  { key: 'olderAdults', name: 'Older Adults', desc: 'Enhance visibility and reading comfort', ico: A11Y_ICONS.olderAdults,
    implies: { readableFont: true, highlightLinks: true, highlightFocus: true, fontScalePct: 120 } }
];

const CONTENT_TOGGLES = [
  { key: 'readableFont', label: 'Readable Font', ico: A11Y_ICONS.readableFont },
  { key: 'highlightTitles', label: 'Highlight Titles', ico: A11Y_ICONS.highlightTitles },
  { key: 'highlightLinks', label: 'Highlight Links', ico: A11Y_ICONS.highlightLinks },
  { key: 'textMagnifier', label: 'Text Magnifier', ico: A11Y_ICONS.textMagnifier }
];

const COLOR_MODES = [
  { key: 'dark-contrast', label: 'Dark Contrast', ico: A11Y_ICONS.darkContrast },
  { key: 'light-contrast', label: 'Light Contrast', ico: A11Y_ICONS.lightContrast },
  { key: 'high-contrast', label: 'High Contrast', ico: A11Y_ICONS.highContrast },
  { key: 'high-saturation', label: 'High Saturation', ico: A11Y_ICONS.highSaturation },
  { key: 'monochrome', label: 'Monochrome', ico: A11Y_ICONS.monochrome },
  { key: 'low-saturation', label: 'Low Saturation', ico: A11Y_ICONS.lowSaturation }
];

const ORIENT_TOGGLES = [
  { key: 'muteSounds', label: 'Mute Sounds', ico: A11Y_ICONS.muteSounds },
  { key: 'hideImages', label: 'Hide Images', ico: A11Y_ICONS.hideImages },
  { key: 'readMode', label: 'Read Mode', ico: A11Y_ICONS.readMode },
  { key: 'readingGuide', label: 'Reading Guide', ico: A11Y_ICONS.readingGuide },
  { key: 'stopAnimations', label: 'Stop Animations', ico: A11Y_ICONS.stopAnimations },
  { key: 'readingMask', label: 'Reading Mask', ico: A11Y_ICONS.readingMask },
  { key: 'highlightHover', label: 'Highlight Hover', ico: A11Y_ICONS.highlightHover },
  { key: 'highlightFocus', label: 'Highlight Focus', ico: A11Y_ICONS.highlightFocus }
];

const LINE_HEIGHTS = [1.8, 2.1, 2.5];
const LETTER_SPACES = ['0.75px', '1.5px', '3px'];
const ALIGN_CYCLE = [null, 'left', 'center', 'right'];

function defaultState() {
  return {
    profiles: Object.fromEntries(PROFILES.map(p => [p.key, false])),
    contentScale: 100,
    readableFont: false,
    highlightTitles: false,
    highlightLinks: false,
    textMagnifier: false,
    fontScalePct: 100,
    lineHeightStep: 0,
    letterSpacingStep: 0,
    align: null,
    colorMode: null,
    textColor: null,
    titleColor: null,
    bgColor: null,
    muteSounds: false,
    hideImages: false,
    readMode: false,
    readingGuide: false,
    stopAnimations: false,
    readingMask: false,
    highlightHover: false,
    highlightFocus: false,
    cursor: null,
    screenReader: false,
    hidden: false
  };
}

let state = defaultState();
let initialized = false;
let panelOpen = false;
let refocusSel = null;
let els = {};

function load() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      const saved = JSON.parse(raw);
      state = {
        ...defaultState(),
        ...saved,
        profiles: { ...defaultState().profiles, ...(saved.profiles || {}) }
      };
    }
  } catch { /* fall back to defaults */ }
}

function save() {
  try { localStorage.setItem(LS_KEY, JSON.stringify(state)); } catch { /* ignore */ }
}

function loadPosition() {
  try { return JSON.parse(localStorage.getItem(POS_KEY)); } catch { return null; }
}

function savePosition(pos) {
  try { localStorage.setItem(POS_KEY, JSON.stringify(pos)); } catch { /* ignore */ }
}

// Is any accessibility adjustment currently active? Drives the badge.
function hasActiveFeatures() {
  if (Object.values(state.profiles).some(v => v)) return true;
  if (state.contentScale !== 100) return true;
  if (state.readableFont) return true;
  if (state.highlightTitles) return true;
  if (state.highlightLinks) return true;
  if (state.textMagnifier) return true;
  if (state.fontScalePct !== 100) return true;
  if (state.lineHeightStep > 0) return true;
  if (state.letterSpacingStep > 0) return true;
  if (state.align) return true;
  if (state.colorMode) return true;
  if (state.textColor) return true;
  if (state.titleColor) return true;
  if (state.bgColor) return true;
  if (state.muteSounds) return true;
  if (state.hideImages) return true;
  if (state.readMode) return true;
  if (state.readingGuide) return true;
  if (state.stopAnimations) return true;
  if (state.readingMask) return true;
  if (state.highlightHover) return true;
  if (state.highlightFocus) return true;
  if (state.cursor) return true;
  return false;
}

function updateTriggerIndicators() {
  if (!els.trigger) return;
  const check = els.trigger.querySelector('.a11y-trigger-check');
  if (check) check.hidden = !hasActiveFeatures();
}

function effective() {
  const e = { ...state, profiles: undefined };
  for (const p of PROFILES) {
    if (!state.profiles[p.key]) continue;
    for (const [k, v] of Object.entries(p.implies)) {
      if (typeof v === 'number') e[k] = Math.max(e[k] || 0, v);
      else if (typeof v === 'boolean') e[k] = e[k] || v;
      else e[k] = e[k] || v;
    }
  }
  return e;
}

function apply() {
  const e = effective();
  const html = document.documentElement;
  const cl = html.classList;

  html.style.fontSize = (e.contentScale && e.contentScale !== 100)
    ? (16 * e.contentScale / 100).toFixed(2) + 'px'
    : '';

  cl.toggle('a11y-font-scale', e.fontScalePct > 100);
  html.style.setProperty('--a11y-fs', (e.fontScalePct / 100).toString());

  const lhOn = e.lineHeightStep > 0;
  cl.toggle('a11y-line-height', lhOn);
  if (lhOn) html.style.setProperty('--a11y-lh', String(LINE_HEIGHTS[e.lineHeightStep - 1]));

  const lsOn = e.letterSpacingStep > 0;
  cl.toggle('a11y-letter-spacing', lsOn);
  if (lsOn) html.style.setProperty('--a11y-ls', LETTER_SPACES[e.letterSpacingStep - 1]);

  cl.remove('a11y-align-left', 'a11y-align-center', 'a11y-align-right');
  if (e.align) cl.add('a11y-align-' + e.align);

  cl.toggle('a11y-readable-font', !!e.readableFont);
  cl.toggle('a11y-highlight-titles', !!e.highlightTitles);
  cl.toggle('a11y-highlight-links', !!e.highlightLinks);

  for (const m of COLOR_MODES) cl.remove('a11y-color-' + m.key);
  if (e.colorMode) cl.add('a11y-color-' + e.colorMode);

  cl.toggle('a11y-custom-text', !!e.textColor);
  if (e.textColor) html.style.setProperty('--a11y-text-color', e.textColor);
  cl.toggle('a11y-custom-title', !!e.titleColor);
  if (e.titleColor) html.style.setProperty('--a11y-title-color', e.titleColor);
  cl.toggle('a11y-custom-bg', !!e.bgColor);
  if (e.bgColor) html.style.setProperty('--a11y-bg-color', e.bgColor);

  cl.toggle('a11y-hide-images', !!e.hideImages);
  cl.toggle('a11y-read-mode', !!e.readMode);
  cl.toggle('a11y-stop-animations', !!e.stopAnimations);
  cl.toggle('a11y-highlight-focus', !!e.highlightFocus);

  cl.remove('a11y-cursor-big-black', 'a11y-cursor-big-white');
  if (e.cursor) cl.add('a11y-cursor-' + e.cursor);

  html.setAttribute('data-a11y-screen-reader', e.screenReader ? 'true' : 'false');

  readingGuide.set(!!e.readingGuide);
  readingMask.set(!!e.readingMask);
  magnifier.set(!!e.textMagnifier);
  hoverHighlight.set(!!e.highlightHover);
  muteSounds.set(!!e.muteSounds);
}

const readingGuide = (() => {
  let el = null;
  function onMove(ev) { if (el) el.style.top = ev.clientY + 'px'; }
  return {
    set(on) {
      if (on && !el) {
        el = document.createElement('div');
        el.className = 'a11y-reading-guide';
        document.body.appendChild(el);
        window.addEventListener('mousemove', onMove);
      } else if (!on && el) {
        window.removeEventListener('mousemove', onMove);
        el.remove(); el = null;
      }
    }
  };
})();

const readingMask = (() => {
  let el = null;
  function onMove(ev) { if (el) el.style.top = ev.clientY + 'px'; }
  return {
    set(on) {
      if (on && !el) {
        el = document.createElement('div');
        el.className = 'a11y-reading-mask';
        el.style.top = (window.innerHeight / 2) + 'px';
        document.body.appendChild(el);
        window.addEventListener('mousemove', onMove);
      } else if (!on && el) {
        window.removeEventListener('mousemove', onMove);
        el.remove(); el = null;
      }
    }
  };
})();

const magnifier = (() => {
  let el = null;
  function onMove(ev) {
    if (!el) return;
    const target = document.elementFromPoint(ev.clientX, ev.clientY);
    if (!target || isWidgetNode(target)) { el.hidden = true; return; }
    let node = target;
    let text = '';
    for (let i = 0; i < 4 && node; i++) {
      const t = (node.innerText || node.textContent || '').trim();
      if (t) { text = t; break; }
      node = node.parentElement;
    }
    if (!text) { el.hidden = true; return; }
    el.textContent = text.length > 220 ? text.slice(0, 220) + '…' : text;
    el.hidden = false;
    const pad = 16;
    let x = ev.clientX + pad;
    let y = ev.clientY + pad;
    const r = el.getBoundingClientRect();
    if (x + r.width + pad > window.innerWidth) x = window.innerWidth - r.width - pad;
    if (y + r.height + pad > window.innerHeight) y = ev.clientY - r.height - pad;
    el.style.left = Math.max(pad, x) + 'px';
    el.style.top = Math.max(pad, y) + 'px';
  }
  return {
    set(on) {
      if (on && !el) {
        el = document.createElement('div');
        el.className = 'a11y-magnifier';
        el.hidden = true;
        document.body.appendChild(el);
        window.addEventListener('mousemove', onMove);
      } else if (!on && el) {
        window.removeEventListener('mousemove', onMove);
        el.remove(); el = null;
      }
    }
  };
})();

const hoverHighlight = (() => {
  let current = null;
  function onOver(ev) {
    const t = ev.target;
    if (isWidgetNode(t)) return;
    if (current && current !== t) current.classList.remove('a11y-hover-target');
    if (t && t.classList) { t.classList.add('a11y-hover-target'); current = t; }
  }
  function clear() {
    if (current) { current.classList.remove('a11y-hover-target'); current = null; }
  }
  return {
    set(on) {
      if (on) {
        document.addEventListener('mouseover', onOver, true);
      } else {
        document.removeEventListener('mouseover', onOver, true);
        clear();
      }
    }
  };
})();

const muteSounds = (() => {
  let observer = null;
  function muteAll() {
    document.querySelectorAll('audio, video').forEach(m => { m.muted = true; });
  }
  return {
    set(on) {
      if (on && !observer) {
        muteAll();
        observer = new MutationObserver(muteAll);
        observer.observe(document.body, { childList: true, subtree: true });
      } else if (!on && observer) {
        observer.disconnect(); observer = null;
        document.querySelectorAll('audio, video').forEach(m => { m.muted = false; });
      }
    }
  };
})();

function isWidgetNode(node) {
  return !!(node && node.closest && node.closest('.a11y-panel, .a11y-trigger, .a11y-magnifier, .a11y-modal-backdrop'));
}

function esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, c =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function checkBadge(on) {
  return on ? `<span class="a11y-tile-check">${A11Y_ICONS.check}</span>` : '';
}

function toggleTile(t, on) {
  return `<button class="a11y-tile ${on ? 'is-on' : ''}" type="button"
      data-a11y-toggle="${t.key}" aria-pressed="${on}">
      ${checkBadge(on)}
      <span class="a11y-tile-ico">${t.ico}</span>
      <span class="a11y-tile-label">${esc(t.label)}</span></button>`;
}

function stepperTile(key, label, ico, display, on) {
  return `<div class="a11y-tile a11y-tile-wide ${on ? 'is-on' : ''}">
      ${checkBadge(on)}
      <span class="a11y-tile-left"><span class="a11y-tile-ico">${ico}</span><span>${esc(label)}</span></span>
      <span class="a11y-stepper">
        <button type="button" data-a11y-step="${key}" data-dir="-1" aria-label="Decrease ${esc(label)}">−</button>
        <span class="a11y-stepper-val">${esc(display)}</span>
        <button type="button" data-a11y-step="${key}" data-dir="1" aria-label="Increase ${esc(label)}">+</button>
      </span></div>`;
}

function colorTile(field, label, ico, val) {
  const on = !!val;
  return `<div class="a11y-tile a11y-tile-wide ${on ? 'is-on' : ''}">
      ${checkBadge(on)}
      <span class="a11y-tile-left"><span class="a11y-tile-ico">${ico}</span><span>${esc(label)}</span></span>
      <span class="a11y-colorpick">
        <input type="color" data-a11y-color="${field}" value="${val || '#2554e0'}" aria-label="${esc(label)}">
        ${on ? `<button type="button" class="a11y-clear" data-a11y-clearcolor="${field}">clear</button>` : ''}
      </span></div>`;
}

function renderBody() {
  const s = state;
  const profiles = PROFILES.map(p => {
    const on = !!s.profiles[p.key];
    return `<div class="a11y-profile ${on ? 'is-on' : ''}" data-a11y-search="${esc(p.name + ' ' + p.desc)}">
        <button type="button" class="a11y-switch" role="switch"
          aria-checked="${on}" aria-label="${esc(p.name)}"
          data-a11y-toggle-profile="${p.key}"></button>
        <span class="a11y-profile-txt">
          <span class="a11y-profile-name">${esc(p.name)}</span>
          <span class="a11y-profile-desc">${esc(p.desc)}</span>
        </span>
        <span class="a11y-profile-ico">${p.ico}</span>
      </div>`;
  }).join('');

  const alignLabel = s.align ? s.align.charAt(0).toUpperCase() + s.align.slice(1) : 'Off';
  const content = [
    stepperTile('contentScale', 'Content Scaling', A11Y_ICONS.contentScale, s.contentScale + '%', s.contentScale !== 100),
    ...CONTENT_TOGGLES.map(t => toggleTile(t, !!s[t.key])),
    stepperTile('fontScalePct', 'Adjust Font Sizing', A11Y_ICONS.fontScale, s.fontScalePct + '%', s.fontScalePct !== 100),
    stepperTile('lineHeightStep', 'Adjust Line Height', A11Y_ICONS.lineHeight,
      s.lineHeightStep ? 'x' + s.lineHeightStep : 'Off', s.lineHeightStep > 0),
    stepperTile('letterSpacingStep', 'Adjust Letter Spacing', A11Y_ICONS.letterSpacing,
      s.letterSpacingStep ? 'x' + s.letterSpacingStep : 'Off', s.letterSpacingStep > 0),
    `<button class="a11y-tile a11y-tile-wide ${s.align ? 'is-on' : ''}" type="button"
        data-a11y-align-cycle aria-label="Align text">
        ${checkBadge(!!s.align)}
        <span class="a11y-tile-left"><span class="a11y-tile-ico">${A11Y_ICONS.align}</span><span>Align Text</span></span>
        <span class="a11y-stepper-val">${alignLabel}</span></button>`
  ].join('');

  const colors = [
    ...COLOR_MODES.map(m =>
      `<button class="a11y-tile ${s.colorMode === m.key ? 'is-on' : ''}" type="button"
          data-a11y-colormode="${m.key}" aria-pressed="${s.colorMode === m.key}">
          ${checkBadge(s.colorMode === m.key)}
          <span class="a11y-tile-ico">${m.ico}</span>
          <span class="a11y-tile-label">${esc(m.label)}</span></button>`).join(''),
    colorTile('textColor', 'Adjust Text Colors', A11Y_ICONS.textColorIco, s.textColor),
    colorTile('titleColor', 'Adjust Title Colors', A11Y_ICONS.titleColorIco, s.titleColor),
    colorTile('bgColor', 'Adjust Background Colors', A11Y_ICONS.bgColorIco, s.bgColor)
  ].join('');

  const cursors = [
    { key: 'big-black', label: 'Big Black Cursor' },
    { key: 'big-white', label: 'Big White Cursor' }
  ].map(c => {
    const on = s.cursor === c.key;
    return `<button class="a11y-tile ${on ? 'is-on' : ''}" type="button"
        data-a11y-cursor="${c.key}" aria-pressed="${on}">
        ${checkBadge(on)}
        <span class="a11y-tile-ico" style="color:${c.key === 'big-black' ? '#1a2233' : '#c7ccd6'}">${A11Y_ICONS.cursor}</span>
        <span class="a11y-tile-label">${esc(c.label)}</span></button>`;
  }).join('');

  const orient = [
    ...ORIENT_TOGGLES.map(t => toggleTile(t, !!s[t.key])),
    `<div class="a11y-tile a11y-tile-wide a11y-links">
        <span class="a11y-tile-left"><span class="a11y-tile-ico">${A11Y_ICONS.links}</span><span>Useful Links</span></span>
        <select data-a11y-links aria-label="Useful links">
          <option value="">Select an option…</option>
          <option value="main">Skip to Main Content</option>
          <option value="nav">Skip to Navigation</option>
          <option value="top">Back to Top</option>
          <option value="home">Home Page</option>
          <option value="submit">Contact Us</option>
          <option value="ada">ADA Statement</option>
        </select></div>`,
    cursors
  ].join('');

  els.body.innerHTML = `
    <section class="a11y-section" data-a11y-section="profiles">
      <p class="a11y-section-lead">Choose the right accessibility profile for you</p>
      ${profiles}
    </section>
    <section class="a11y-section" data-a11y-section="content">
      <h3 class="a11y-section-title">Content Adjustments</h3>
      <div class="a11y-tiles">${content}</div>
    </section>
    <section class="a11y-section" data-a11y-section="color">
      <h3 class="a11y-section-title">Color Adjustments</h3>
      <div class="a11y-tiles">${colors}</div>
    </section>
    <section class="a11y-section" data-a11y-section="orientation">
      <h3 class="a11y-section-title">Orientation Adjustments</h3>
      <div class="a11y-tiles">${orient}</div>
    </section>`;

  if (refocusSel) {
    const el = els.body.querySelector(refocusSel);
    if (el) el.focus();
    refocusSel = null;
  }
}

function commit(sel) {
  if (sel) refocusSel = sel;
  save();
  apply();
  updateTriggerIndicators();
  if (panelOpen) renderBody();
}

function setPanel(open) {
  panelOpen = open;
  if (els.panel) els.panel.hidden = !open;
  if (els.trigger) els.trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
  if (open) {
    renderBody();
    const search = els.panel.querySelector('[data-a11y-search-input]');
    if (search) search.focus();
  } else if (els.trigger) {
    els.trigger.focus();
  }
}

function resetAll() {
  state = defaultState();
  try { localStorage.removeItem(POS_KEY); } catch { /* ignore */ }
  if (els.trigger) {
    els.trigger.style.left = '';
    els.trigger.style.top = '';
    els.trigger.style.right = '';
    els.trigger.style.bottom = '';
  }
  commit();
}

function hideWidget() {
  state.hidden = true;
  save();
  setPanel(false);
  if (els.trigger) els.trigger.hidden = true;
}

function filterSearch(q) {
  const query = (q || '').trim().toLowerCase();
  els.body.querySelectorAll('.a11y-profile, .a11y-tile').forEach(el => {
    const hay = (el.getAttribute('data-a11y-search')
      || el.textContent
      || '').toLowerCase();
    el.style.display = !query || hay.includes(query) ? '' : 'none';
  });
}

function handleUsefulLink(value) {
  const map = {
    main: () => {
      const main = document.querySelector('main, [role="main"], .app') || document.body;
      main.setAttribute('tabindex', '-1');
      main.focus({ preventScroll: false });
      main.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    nav: () => {
      const nav = document.querySelector('header, nav, .header') || document.body;
      nav.setAttribute('tabindex', '-1');
      nav.focus({ preventScroll: false });
      nav.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    top: () => window.scrollTo({ top: 0, behavior: 'smooth' }),
    home: () => { window.location.href = '/'; },
    submit: () => { window.location.href = '/contactme'; },
    ada: () => { window.location.href = '/ada-compliant'; }
  };
  if (map[value]) map[value]();
}

function openStatement() {
  const existing = document.querySelector('.a11y-modal-backdrop');
  if (existing) existing.remove();

  const backdrop = document.createElement('div');
  backdrop.className = 'a11y-modal-backdrop';
  backdrop.innerHTML = `
    <div class="a11y-modal" role="dialog" aria-modal="true" aria-labelledby="a11y-statement-title">
      <h2 id="a11y-statement-title">Accessibility Statement</h2>
      <p>
        For visitors with special needs, we will gladly provide any assistance we can to
        accommodate your needs.
      </p>
      <p>
        WebaNova is committed to making this website accessible to all users,
        including people with disabilities. We aim to follow accessibility best practices and
        continuously improve the user experience for everyone.
      </p>
      <h3>Need help?</h3>
      <p>
        If you experience difficulty accessing any part of this website, please
        <a href="/contactme">contact us</a> or call
        <a href="tel:+18186419709">(818) 641-9709</a>.
        You can also read our full statement on the
        <a href="/ada-compliant">ADA Compliant</a> page.
      </p>
      <div class="a11y-modal-close-row">
        <button type="button" data-a11y-close-modal>Close</button>
      </div>
    </div>`;

  function close() {
    backdrop.remove();
    document.removeEventListener('keydown', onKey);
  }
  function onKey(ev) {
    if (ev.key === 'Escape') close();
  }

  backdrop.addEventListener('click', (ev) => {
    if (ev.target === backdrop || ev.target.closest('[data-a11y-close-modal]')) close();
  });
  document.addEventListener('keydown', onKey);
  document.body.appendChild(backdrop);
  backdrop.querySelector('[data-a11y-close-modal]')?.focus();
}

function onPanelClick(ev) {
  const t = ev.target.closest('[data-a11y-toggle-profile], [data-a11y-toggle], [data-a11y-step], [data-a11y-colormode], [data-a11y-cursor], [data-a11y-align-cycle], [data-a11y-clearcolor], [data-a11y-close], [data-a11y-reset], [data-a11y-statement], [data-a11y-hide]');
  if (!t) return;

  if (t.hasAttribute('data-a11y-close')) { setPanel(false); return; }
  if (t.hasAttribute('data-a11y-reset')) { resetAll(); return; }
  if (t.hasAttribute('data-a11y-statement')) { openStatement(); return; }
  if (t.hasAttribute('data-a11y-hide')) { hideWidget(); return; }

  const profile = t.getAttribute('data-a11y-toggle-profile');
  if (profile) {
    state.profiles[profile] = !state.profiles[profile];
    commit(`[data-a11y-toggle-profile="${profile}"]`);
    return;
  }

  const toggle = t.getAttribute('data-a11y-toggle');
  if (toggle) {
    state[toggle] = !state[toggle];
    commit(`[data-a11y-toggle="${toggle}"]`);
    return;
  }

  const step = t.getAttribute('data-a11y-step');
  if (step) {
    const dir = Number(t.getAttribute('data-dir') || 1);
    if (step === 'contentScale') {
      state.contentScale = Math.min(200, Math.max(80, state.contentScale + dir * 10));
    } else if (step === 'fontScalePct') {
      state.fontScalePct = Math.min(200, Math.max(100, state.fontScalePct + dir * 10));
    } else if (step === 'lineHeightStep') {
      state.lineHeightStep = Math.min(3, Math.max(0, state.lineHeightStep + dir));
    } else if (step === 'letterSpacingStep') {
      state.letterSpacingStep = Math.min(3, Math.max(0, state.letterSpacingStep + dir));
    }
    commit(`[data-a11y-step="${step}"][data-dir="${dir}"]`);
    return;
  }

  const mode = t.getAttribute('data-a11y-colormode');
  if (mode) {
    state.colorMode = state.colorMode === mode ? null : mode;
    commit(`[data-a11y-colormode="${mode}"]`);
    return;
  }

  const cursor = t.getAttribute('data-a11y-cursor');
  if (cursor) {
    state.cursor = state.cursor === cursor ? null : cursor;
    commit(`[data-a11y-cursor="${cursor}"]`);
    return;
  }

  if (t.hasAttribute('data-a11y-align-cycle')) {
    const i = ALIGN_CYCLE.indexOf(state.align);
    state.align = ALIGN_CYCLE[(i + 1) % ALIGN_CYCLE.length];
    commit('[data-a11y-align-cycle]');
    return;
  }

  const clear = t.getAttribute('data-a11y-clearcolor');
  if (clear) {
    state[clear] = null;
    commit();
  }
}

function onPanelChange(ev) {
  const color = ev.target.getAttribute?.('data-a11y-color');
  if (color) {
    state[color] = ev.target.value;
    commit();
    return;
  }
  if (ev.target.hasAttribute?.('data-a11y-links')) {
    const value = ev.target.value;
    if (value) {
      handleUsefulLink(value);
      ev.target.value = '';
    }
  }
}

function onPanelInput(ev) {
  if (ev.target.hasAttribute?.('data-a11y-search-input')) {
    filterSearch(ev.target.value);
  }
}

// Make the trigger draggable via its small handle, keeping it on-screen
// and persisting the resting position in localStorage.
function attachDrag(trigger) {
  const handle = trigger.querySelector('.a11y-trigger-drag');
  if (!handle) return;

  let dragging = false;
  let startX = 0, startY = 0, startLeft = 0, startTop = 0;

  handle.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = trigger.getBoundingClientRect();
    dragging = true;
    startX = e.clientX;
    startY = e.clientY;
    startLeft = rect.left;
    startTop = rect.top;
    handle.setPointerCapture(e.pointerId);
  });

  handle.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    const rect = trigger.getBoundingClientRect();
    const MARGIN = 8;
    let left = startLeft + (e.clientX - startX);
    let top = startTop + (e.clientY - startY);
    left = Math.max(MARGIN, Math.min(left, window.innerWidth - rect.width - MARGIN));
    top = Math.max(MARGIN, Math.min(top, window.innerHeight - rect.height - MARGIN));
    trigger.style.left = `${left}px`;
    trigger.style.top = `${top}px`;
    trigger.style.right = 'auto';
    trigger.style.bottom = 'auto';
  });

  handle.addEventListener('pointerup', () => {
    if (!dragging) return;
    dragging = false;
    savePosition({ left: trigger.style.left, top: trigger.style.top });
  });
}

function mount() {
  if (document.querySelector('.a11y-trigger')) return;

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'a11y-trigger';
  trigger.setAttribute('aria-label', 'Open accessibility options');
  trigger.setAttribute('aria-expanded', 'false');
  trigger.setAttribute('aria-controls', 'a11y-panel');
  trigger.innerHTML = `
    <span class="a11y-trigger-check" hidden>${A11Y_ICONS.check}</span>
    <span class="a11y-trigger-drag" aria-hidden="true">⋮⋮</span>
    ${TRIGGER_SVG}`;
  trigger.hidden = !!state.hidden;

  const panel = document.createElement('div');
  panel.id = 'a11y-panel';
  panel.className = 'a11y-panel';
  panel.hidden = true;
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-label', 'Accessibility options');
  panel.innerHTML = `
    <div class="a11y-header">
      <div class="a11y-header-top">
        <h2 class="a11y-title">Accessibility Adjustments</h2>
        <button type="button" class="a11y-close" data-a11y-close aria-label="Close accessibility panel">
          ${A11Y_ICONS.close}
        </button>
      </div>
      <div class="a11y-header-actions">
        <button type="button" data-a11y-reset>${A11Y_ICONS.reset}<span>Reset Settings</span></button>
        <button type="button" data-a11y-statement>${A11Y_ICONS.statement}<span>Statement</span></button>
        <button type="button" data-a11y-hide>${A11Y_ICONS.hide}<span>Hide Forever</span></button>
      </div>
      <div class="a11y-search">
        <span class="a11y-search-ico">${A11Y_ICONS.search}</span>
        <input type="search" data-a11y-search-input placeholder="Search adjustments…" aria-label="Search adjustments">
      </div>
    </div>
    <div class="a11y-body"></div>
    <div class="a11y-footer">
      <span>WebaNova</span>
      <a href="/accessibility">ADA Statement →</a>
    </div>`;

  els = {
    trigger,
    panel,
    body: panel.querySelector('.a11y-body')
  };

  trigger.addEventListener('click', () => setPanel(!panelOpen));
  panel.addEventListener('click', onPanelClick);
  panel.addEventListener('change', onPanelChange);
  panel.addEventListener('input', onPanelInput);

  document.addEventListener('keydown', (ev) => {
    if (ev.key === 'Escape' && panelOpen) setPanel(false);
  });

  document.body.appendChild(trigger);
  document.body.appendChild(panel);

  // Restore any saved drag position (clamped to the current viewport).
  const savedPos = loadPosition();
  if (savedPos) {
    const left = parseInt(savedPos.left, 10) || 0;
    const top = parseInt(savedPos.top, 10) || 0;
    const maxLeft = window.innerWidth - trigger.offsetWidth;
    const maxTop = window.innerHeight - trigger.offsetHeight;
    trigger.style.left = `${Math.max(0, Math.min(left, maxLeft))}px`;
    trigger.style.top = `${Math.max(0, Math.min(top, maxTop))}px`;
    trigger.style.right = 'auto';
    trigger.style.bottom = 'auto';
  }

  attachDrag(trigger);
}

export function initAccessibilityWidget() {
  if (initialized) return;
  initialized = true;
  load();
  mount();
  apply();
  updateTriggerIndicators();
}
